/**
 * Fixes the position of a MUI Tabs indicator rendered inside a Menu or Popover.
 *
 * <p>A Menu opens with a Grow transition, which starts the paper at {@code scale(0.75, 0.5625)}
 * and animates it to full size. Tabs measures the selected tab with
 * {@code getBoundingClientRect}, and that returns *scaled* geometry — so a menu opened on its
 * second tab placed the underline at 0.75x the tab's real offset (measured: 67.5px instead of
 * 90px), leaving it stranded between the two tabs. Nothing re-measures when the transition
 * finishes, so it stayed wrong until some unrelated re-render — a notification poll, a hover —
 * happened to run the effect again, at which point the underline slid into place seemingly on
 * its own. The first tab hid the bug: 0.75 x 0 is still 0.
 *
 * <p>The fix re-measures once the paper has stopped growing, and keeps the indicator hidden with
 * its transition suppressed until then. The correction therefore lands as a paint rather than an
 * animation, and no wrong position is ever shown: the underline simply fades in where it belongs.
 * Once settled, the override is dropped entirely so switching tabs by hand animates as designed.
 *
 * <p>Both handlers hang off the transition's own lifecycle — hiding on enter, settling on
 * entered — rather than off whatever opens the menu. That way the pair cannot come apart: a
 * click that does not actually start a transition (the trigger pressed while the menu is
 * already open) leaves the indicator alone instead of hiding it with no entry left to reveal it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TabsActions } from '@mui/material';

export interface MenuTabsIndicator {
  /** Pass to {@code <Tabs action={...}>} so the indicator can be re-measured on demand. */
  action: React.RefObject<TabsActions | null>;
  /** Spread into the Tabs {@code sx}. Empty once the indicator has been measured at full size. */
  indicatorSx: Record<string, unknown>;
  /** Wire to the transition's {@code onEnter}: the paper has started growing. */
  handleEnter: () => void;
  /** Wire to the transition's {@code onEntered}: the paper has reached full size. */
  handleEntered: () => void;
}

const HIDDEN_UNTIL_MEASURED = {
  '& .MuiTabs-indicator': { transition: 'none', opacity: 0 },
} as const;

const MEASURED = {};

export function useMenuTabsIndicator(): MenuTabsIndicator {
  const action = useRef<TabsActions | null>(null);
  const frame = useRef<number | null>(null);
  // Bumped every time the paper starts growing. A frame scheduled for an earlier entry is
  // ignored when it fires, so settling never depends on cancelAnimationFrame having taken
  // effect in time.
  const entering = useRef(0);
  const [measured, setMeasured] = useState(false);

  const cancelFrame = useCallback(() => {
    if (frame.current !== null) {
      cancelAnimationFrame(frame.current);
      frame.current = null;
    }
  }, []);

  const handleEnter = useCallback(() => {
    entering.current += 1;
    cancelFrame();
    setMeasured(false);
  }, [cancelFrame]);

  const handleEntered = useCallback(() => {
    // Re-measure while the indicator is still hidden and unanimated, so the corrected
    // position is simply painted. Only the following frame restores the transition, which
    // is why the correction itself never animates.
    action.current?.updateIndicator();
    cancelFrame();
    const scheduledFor = entering.current;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      if (entering.current !== scheduledFor) {
        return;
      }
      setMeasured(true);
    });
  }, [cancelFrame]);

  // A menu can be unmounted mid-transition (route change, logout) — never leave a frame
  // pending that would set state on a gone component.
  useEffect(() => cancelFrame, [cancelFrame]);

  return {
    action,
    indicatorSx: measured ? MEASURED : HIDDEN_UNTIL_MEASURED,
    handleEnter,
    handleEntered,
  };
}
